import React, { useEffect, useState } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';

import {
    GamePlayerStatus, Texture, TurnStatus,
} from '../../../Constants';
import { ASSETS_URL } from '../../../Settings';
import {
    currentActionGrid,
    disabledActionGridIndexes,
    factionActionIdForTexture,
    isMainHexActionTexture,
    isRandomActionTexture,
} from '../../TwoD/Common/ActionHexUtilities';
import { getTextureUrl } from '../../TwoD/Common/TextureTools';
import {
    whichStacksHaveThisAction,
} from '../../Game/GamePlayerInfo';

import './ActionPicker.css';
import '../../TwoD/Components/Controls/Actions/action-scroller.css';
import '../../TwoD/Components/Controls/Actions/action-selector.css';

function effectiveGamePlayerData(props) {
    // Read from the seated controller for the local seat — the
    // controller's gamePlayerData carries the localIntentPosition
    // override (see GamePlayerController.updateGamePlayerData), so
    // it stays correct during the WS round-trip after a local
    // rotate. Fall back to the React prop for observer mounts.
    const { app, gamePlayerData } = props;
    const seated = app && app.game && app.game.seatedController;
    if (seated && gamePlayerData
            && gamePlayerData.seat === seated.seat
            && seated.gamePlayerData) {
        return seated.gamePlayerData;
    }
    return gamePlayerData;
}

function ActionImage({ texture }) {
    return <img src={getTextureUrl(texture)} alt={'action'}
                data-tname={texture}/>;
}

// ActionPicker — functional-component port of ActionSelector. Renders
// the swiper of available actions and the in-picker End Turn
// fallback for the hex-exhausted-but-bonus-remains case. The window-
// level EndTurnButton (rendered by ActionWindow) handles the
// general mid-turn / juiced-sub-state case; this in-picker copy is
// kept so the legacy parity for the "no remaining grid items" branch
// is unbroken. Both can render simultaneously without conflict.
//
// Per the source's hard-won lessons:
//
// - currentActionGrid is recomputed on EVERY render AND on EVERY
//   click. The render path and onClick recompute from live state
//   independently. A stored grid (instance field, useMemo, useRef)
//   lags the controller by at least one cycle and fires the wrong
//   action under fast tap / parent re-render / WS echo arriving
//   between events.
//
// - Per-seat tick: every game-player-{seat}-event fires
//   setState({tick: tick+1}) to re-render. The earlier listener
//   gated on action_hex_position equality and used a narrow
//   shouldComponentUpdate — silently dropping updates to other
//   grid-relevant fields (available_actions, locked_action_stacks,
//   station, action_tokens). That stale grid was the action-tap-
//   fires-wrong-action symptom.
//
// - Status guard on onClick: TAKING_TURN_ID or WAITING_ID only.
//   The "AAA Advertisement Choice Error" guard from 2026-05-16.
//
// - actionGridIndex null/OOB guard: the 2026-05-22 blank-area-click
//   guard. Swiper's onClick fires for any click landing on the
//   swiper element, not just on slides; without the guard, the
//   backend receives malformed packages.
export function ActionPicker(props) {
    const { gameData } = props;
    const liveProps = props;
    const seat = props.gamePlayerData
        ? props.gamePlayerData.seat : null;

    // tick is a force-render counter wired to the per-seat event.
    // The earlier gate on position equality is the bug this fixes —
    // we ALWAYS re-render on every fire and we ALWAYS recompute the
    // grid from live state in the render body.
    const [, setTick] = useState(0);

    useEffect(() => {
        if (seat === null || seat === undefined) return undefined;
        const eventName = 'game-player-' + seat + '-event';
        const onSeatTick = () => {
            setTick((t) => t + 1);
        };
        window.addEventListener(eventName, onSeatTick);
        return () => {
            window.removeEventListener(eventName, onSeatTick);
        };
    }, [seat]);

    const api = (liveProps.app && liveProps.app.game
            && liveProps.app.game.seatedController
            && liveProps.app.game.seatedController.gamePlayerApi)
        ? liveProps.app.game.seatedController.gamePlayerApi : null;
    const seatedController = (liveProps.app && liveProps.app.game
            && liveProps.app.game.seatedController)
        ? liveProps.app.game.seatedController : null;

    const onClick = (swiper) => {
        // Read live from the controller, recompute grid fresh.
        // The render path's grid is a separate, equally-fresh
        // computation — we deliberately do not share it.
        const gamePlayerData = effectiveGamePlayerData(liveProps);
        if (!gamePlayerData) return;
        const liveActionGrid = currentActionGrid(gamePlayerData);
        const actionGridIndex = swiper.clickedIndex;

        // Guard against blank-area clicks in the swiper container.
        // Swiper's onClick fires for any click landing on the
        // swiper element, not just on slides; clickedIndex is
        // undefined for whitespace / padding. Without this guard,
        // actionRoomTexture below is undefined too, and the
        // backend receives malformed packages.
        if (actionGridIndex === null
                || actionGridIndex === undefined
                || actionGridIndex < 0
                || actionGridIndex >= liveActionGrid.length) {
            return;
        }
        const actionRoomTexture = liveActionGrid[actionGridIndex];
        if (actionRoomTexture === null
                || actionRoomTexture === undefined) {
            return;
        }

        // Manual §7.1: during a Main Action Set the non-active stack
        // and the bonus tail (faction tokens + random actions) are not
        // tappable. Recompute the disabled set live — render and
        // onClick each derive it independently from live state; we do
        // not share or cache it.
        const liveDisabled = disabledActionGridIndexes(gamePlayerData);
        if (liveDisabled.includes(actionGridIndex)) {
            console.warn(
                '[ActionPicker] Suppressing action-grid click on disabled '
                + 'index ' + actionGridIndex + ' (MAIN_SET non-active '
                + 'stack or bonus tail).');
            return;
        }

        // Status guard. status and turn_status are written by
        // separate engine calls; a transition can leave turn_status
        // at SELECT_ACTION_ID for one cycle after status flipped to
        // (e.g.) ADVERTISING_ID. GamePlayerPackageSender's allow-
        // list lets the misrouted package through. Block at source.
        const status = gamePlayerData.status;
        if (status !== GamePlayerStatus.TAKING_TURN_ID
                && status !== GamePlayerStatus.WAITING_ID) {
            console.warn(
                '[ActionPicker] Suppressing action-grid click — '
                + 'seat is in status ' + status + ' which does not '
                + 'accept action-grid clicks (expected '
                + 'TAKING_TURN_ID or WAITING_ID).');
            return;
        }

        const turnStatus = gamePlayerData.turn_status;
        if (turnStatus === TurnStatus.SELECT_ACTION_ID) {
            const actionId = Texture.ACTIONS.indexOf(actionRoomTexture);
            const stackData = whichStacksHaveThisAction(
                gamePlayerData, actionId);
            let stack = null;
            if (actionId < 7) {
                stack = 0;
                if (!stackData.stacks[0]) stack = 1;
            }
            if (api) {
                api.selectActionStack(
                    actionGridIndex, actionRoomTexture, stack);
            }
            return;
        }
        if (turnStatus === TurnStatus.CHOOSE_ACTIONS_ID) {
            const notLocked = !gamePlayerData.action_hex_locked;
            if (!notLocked) {
                console.warn('Choose when locked.');
                return;
            }
            if (isMainHexActionTexture(actionRoomTexture)) {
                if (seatedController && seatedController.lockTheActionHex) {
                    seatedController.lockTheActionHex(
                        gamePlayerData.action_hex_position);
                }
                if (api) api.lockAndClickActionGrid(actionRoomTexture);
                return;
            }
            const factionActionId = factionActionIdForTexture(actionRoomTexture);
            if (factionActionId !== null) {
                if (api) api.takeFactionAction(factionActionId);
                return;
            }
            if (isRandomActionTexture(actionRoomTexture)) {
                if (api) api.takeRandomAction();
                return;
            }
            console.warn(
                '[ActionPicker] Unknown action-grid texture in '
                + 'CHOOSE_ACTIONS_ID: ' + actionRoomTexture);
        }
    };

    const gamePlayerData = effectiveGamePlayerData(liveProps);
    if (!gamePlayerData) return null;

    const newActionGrid = currentActionGrid(gamePlayerData);
    const disabledIndexes = disabledActionGridIndexes(gamePlayerData);

    // Hex-exhausted with no remaining grid items: render nothing.
    // The window-level EndTurnButton (rendered by ActionWindow) is
    // the canonical End Turn surface — duplicating it here as a
    // full-region fallback stacks two buttons in ActionWrapper's
    // flex column and pushes the second copy off the bottom of the
    // FloatingWindow. The legacy fallback lives in ActionSelector
    // for the mobile / other-preset paths; Desktop relies on the
    // window-level button exclusively.
    if (newActionGrid.length === 0) return null;

    return (
        <div className={'action-scroller action-picker'}
             data-testid={'action-picker'}>
            <div className={'action-selector-anchor'}>
                <div className={'action-selector-swiper-container'}>
                    <Swiper
                        slidesPerView={6}
                        spaceBetween={0}
                        grabCursor={true}
                        onClick={onClick}
                        className={'action-scroller-swiper'}>
                        {newActionGrid.map((gridItem, index) => {
                            const isDisabled = disabledIndexes
                                .includes(index);
                            const slideClass = isDisabled
                                ? 'action-picker-slide-disabled' : '';
                            return (
                                <SwiperSlide key={'gi-' + index}
                                             virtualIndex={index}
                                             className={slideClass}>
                                    <ActionImage texture={gridItem}/>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
                <img
                    src={ASSETS_URL
                        + 'overlays/ActionSelectorOverlay-1080x210.png'}
                    alt={'action'}
                    className={'swiper-overlay'}/>
            </div>
        </div>
    );
}

export default ActionPicker;
